import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useStore } from '../../hooks/storeContext';
import { ROUTES } from '../../constants/routes';
import { LoadingContainer } from './styled';

const RequireAdmin = observer(({ children }) => {
  const { authStore } = useStore();
  const location = useLocation();
  const token = localStorage.getItem('bhaAuthToken');
  // Only show the verifying screen when the user still needs to be fetched.
  // Otherwise it flashes a full-page light background on every navigation.
  const [loading, setLoading] = useState(() => !!token && !authStore.me);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      if (authStore.me) {
        setLoading(false);
        return;
      }

      try {
        await authStore.whoami();
      } catch (error) {
        // whoami handles 401 cleanup internally — don't force a full logout
        // on transient errors (network blips, timeouts, etc.)
        console.error('Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [authStore, token]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Verifying admin access..." />
      </LoadingContainer>
    );
  }

  // No token - redirect to login
  if (!token) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // No user data - redirect to login
  if (!authStore.me) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // User exists but not admin - redirect to login with error
  if (authStore.me.role !== 'admin') {
    authStore.logout();
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // User is admin - render protected component
  return children;
});

export default RequireAdmin;
